import { JSX } from "react";
import { Dropdown, Modal, Spinner } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DashboardTable from "../table/DashboardTable";

type PaginationData = {
  page?: number;
  page_size?: number;
  total?: number;
  total_pages?: number;
};

type WidgetDownloadFormat = "xlsx" | "csv";

type DataTableModalProps = {
  title: string;
  show: boolean;
  tableData: unknown;
  pagination?: PaginationData;
  isLoading?: boolean;
  isDownloading?: boolean;
  onDownload?: (format: WidgetDownloadFormat) => void;
  onPageChange?: (page: number) => void;
  handleClose: () => void;
};

export default function DataTableModal({
  title,
  tableData,
  pagination,
  isLoading = false,
  isDownloading = false,
  onDownload,
  onPageChange,
  show,
  handleClose,
}: DataTableModalProps): JSX.Element {
  const allRows = tableData as Record<string, unknown>[];
  const page = pagination?.page ?? 1;
  const totalPages = pagination?.total_pages ?? 1;
  const shouldShowPagination = totalPages > 1 && !!onPageChange;

  return (
    <Modal
      show={show}
      size="xl"
      centered
      onHide={handleClose}
      dialogClassName="modal-xxl"
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center justify-content-between w-100 me-4">
          <Modal.Title>{title}</Modal.Title>
          {onDownload && (
            <Dropdown align="end">
              <Dropdown.Toggle
                className="btn btn-success btn-sm"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Spinner
                    className="me-1"
                    animation="border"
                    size="sm"
                    role="status"
                  >
                    <span className="visually-hidden">Downloading...</span>
                  </Spinner>
                ) : (
                  <>
                    <i className="bi bi-download"></i>&nbsp;
                  </>
                )}
                Download
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  as="button"
                  onClick={() => onDownload("xlsx")}
                  disabled={isDownloading}
                >
                  XLSX
                </Dropdown.Item>
                <Dropdown.Item
                  as="button"
                  onClick={() => onDownload("csv")}
                  disabled={isDownloading}
                >
                  CSV
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ height: "75vh", overflow: "auto" }}>
        <DashboardTable
          reportHeader={Object.keys(
            (allRows?.length > 0 ? allRows[0] : {}) as Record<string, unknown>,
          )}
          isReportLoading={isLoading}
          reportData={allRows ?? []}
          responsive={false}
        />
      </Modal.Body>
      {shouldShowPagination && (
        <Modal.Footer className="justify-content-center py-2 pagination-nav">
          <ReactPaginate
            breakLabel="..."
            nextLabel={
              <>
                Next <i className="bi bi-chevron-right"></i>
              </>
            }
            onPageChange={({ selected }: { selected: number }) =>
              onPageChange?.(selected + 1)
            }
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={totalPages}
            previousLabel={
              <>
                <i className="bi bi-chevron-left"></i> Previous
              </>
            }
            forcePage={Math.max(page - 1, 0)}
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            containerClassName="pagination mb-0"
            activeClassName="active"
          />
        </Modal.Footer>
      )}
    </Modal>
  );
}
